import React, { useEffect, useState } from 'react';
import { GameInterface } from '../constants/GameInterface';
import DisclaimerDialog from '../dialogs/DisclaimerDialog';
import LoadingComponent from '../commonComponents/LoadingComponent';
import { Genders } from '../constants/Genders';
import { CODE_LENGTH, MAX_NUM_OF_PLAYERS } from '../constants/Constants';
import { Game } from '../classes/Game';
import { generateCode } from '../utils/GenerateCodeUtil';
import { getAvatar } from '../utils/AvatarUtils';
import { Stats } from '../classes/Stats';
import { Player } from '../classes/Player';
import { Round } from '../classes/Round';
import { UsedRedCards } from '../classes/UsedRedCards';
import { Background } from '../classes/Background';
import { Hand } from '../classes/Hand';
import { RoundBlackCard } from '../classes/RoundBlackCards';
import { Score } from '../classes/Score';
import { Dialogs } from '../constants/Dialogs';
import { getActivePlayersCount } from '../utils/GetActivePlayersCount';
import HeaderInGame from '../HeaderInGame';
import BackButtonComponent from '../commonComponents/BackButtonComponent';
import { Email } from '../classes/Email';
import { PageView } from '../classes/PageView';
import { Pages } from '../constants/Pages';

function InfoDialog({dialog, setDialog, isHost, setIsHost, gameId, setGameId, playerId, setPlayerId, statsId, setStatsId}: GameInterface) {
    const [gameCode, setGameCode] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [isGameCodeError, setIsGameCodeError] = useState(false);
    const [isNameError, setIsNameError] = useState(false);
    const [isEmailError, setIsEmailError] = useState(false);
    const [isGenderError, setIsGenderError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [gender, setGender] = useState<Genders>(Genders.none);
    const [shouldShowDisclaimerDialog, setShouldShowDisclaimerDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleGameCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGameCode(e.target.value);
    }

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    }

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
    }

    const handleSelectGender = (newGender: Genders) => {
        setGender(newGender);
    }

    const closeDialog = () => {
        setShouldShowDisclaimerDialog(false);
    }

    const startGame = async () => {
        setShouldShowDisclaimerDialog(false);
        setIsSubmitting(true);
        const newGameCode = generateCode();
        const games = await Game.getGames(newGameCode);
        if (games.length > 0) {
            // very unlikely scenario, when the generated code already exists
            setErrorMessage("Something went wrong. Try again.");
            setIsSubmitting(false);
            return;
        }

        var unixTime = Date.now();
        const avatar = getAvatar([]);
        const trimmedName = name.trim();
        const trimmedEmail = email.trim();

        const stats = await Stats.addNewStats(gender);
        const game = await Game.addNewGame(newGameCode, unixTime, stats.id);
        const player = await Player.addNewPlayer(trimmedName, 1, gender, unixTime, avatar, game.id);
        await Round.addNewRound(game.id);
        await UsedRedCards.addNewUsedRedCardsRow(game.id);
        await Background.addNewBackground(game.id);
        await Hand.addNewHand(game.id, player.id);
        await RoundBlackCard.addNewRoundBlackCard(game.id, player.id);
        await Score.addNewScore(game.id, player.id);
        await Email.addNewEmail(trimmedEmail);
        
        setIsSubmitting(false);

        setGameId(newGameCode);
        setPlayerId(player.id)
        setStatsId(stats.id);
        setDialog(Dialogs.waitingRoom);
    }

    const joinGame = async () => {
        setShouldShowDisclaimerDialog(false);
        setIsSubmitting(true);
        const trimmedGameCode = gameCode.toUpperCase().trim();
        const trimmedName = name.trim();
        const trimmedEmail = email.trim();
        const games = await Game.getGames(trimmedGameCode);
        if (games.length === 0 || games[0].status === 2) {
            setIsGameCodeError(true);
            setErrorMessage("Invalid code");
            setIsSubmitting(false);
            return;
        }
    
        if (games[0].status === 1) {
            setErrorMessage("Game in progress");
            setIsSubmitting(false);
            return;
        }
    
        const players = await Player.getActivePlayersNameAndImages(games[0].id);
        const count = getActivePlayersCount(players);
        if (count >= MAX_NUM_OF_PLAYERS) {
            setErrorMessage("Game is full");
            setIsSubmitting(false);
            return;
        }
    
        const idx = players.findIndex((p) => p.name.toLowerCase() === trimmedName.toLowerCase());
        if (idx !== -1)
        {
            setIsNameError(true);
            setErrorMessage("Name in use");
            setIsSubmitting(false);
            return;
        }
    
        var unixTime = Date.now();
        const avatar = getAvatar(players);
    
        const player = await Player.addNewPlayer(trimmedName, 1, gender, unixTime, avatar, games[0].id);
        await Hand.addNewHand(games[0].id, player.id);
        await RoundBlackCard.addNewRoundBlackCard(games[0].id, player.id);
        await Score.addNewScore(games[0].id, player.id);
        await Email.addNewEmail(trimmedEmail);
    
        const statsId = games[0].stats;
        Stats.updateGenderCounts(statsId, gender);
    
        setIsSubmitting(false);

        setGameId(trimmedGameCode);
        setPlayerId(player.id)
        setStatsId(statsId);
        setDialog(Dialogs.waitingRoom);
    }

    const submit = async () => {
        const trimmedGameCode = gameCode.toUpperCase().trim();
        const trimmedName = name.trim();
        const trimmedEmail = email.trim();
        let validationMessage = "";

        if (!isHost && trimmedGameCode.length !== CODE_LENGTH) {
            setIsGameCodeError(true);
            validationMessage += "Invalid code & ";
        } else {
            setIsGameCodeError(false);
        }

        if (trimmedName === "") {
            setIsNameError(true);
            validationMessage += "No name chosen & ";
        } else {
            setIsNameError(false);
        }

        if (trimmedEmail === "") {
            setIsEmailError(true);
            validationMessage += "No email chosen & ";
        } else {
            setIsEmailError(false);
        }

        if (gender === Genders.none) {
            setIsGenderError(true);
            validationMessage += "No gender selected & ";
        } else {
            setIsGenderError(false);
        }

        if (validationMessage === "") {
            // setShouldShowDisclaimerDialog(true);
            if (isHost) {
                await startGame();
            } else {
                await joinGame();
            }
        } else {
            validationMessage = validationMessage.substring(0, validationMessage.length - 3); // remove the last 3 chars ( & )
            setErrorMessage(validationMessage);
        }
    }

    const handleBackClick = () => {
        setDialog(Dialogs.none);
    }

    useEffect(() => {
        PageView.addNewPageView(Pages.Info);
    }, [])

    return (
        <div className="overallWrapper">
            <HeaderInGame/>
            <div className="infoOuterWrapper">
                <BackButtonComponent handleBackClick={handleBackClick} />
                <div className="infoInnerWrapper">
                    <div className="infoTitle">Welcome!</div>
                    {!isHost && <input className="infoCode"
                        type="text"
                        style={{borderColor: `${isGameCodeError ? "#D30000" : "black"}`}}
                        value={gameCode}
                        onChange={(e) => handleGameCodeChange(e)}
                        placeholder="Game Code"/>}
                    <input className="infoName"
                        type="text"
                        style={{borderColor: `${isNameError ? "#D30000" : "black"}`}}
                        value={name}
                        onChange={(e) => handleNameChange(e)}
                        placeholder="Name"/>
                    <input className="infoEmail"
                        type="text"
                        style={{borderColor: `${isEmailError ? "#D30000" : "black"}`}}
                        value={email}
                        onChange={(e) => handleEmailChange(e)}
                        placeholder="Email"/>
                    <div className="infoGender" style={{color: `${isGenderError ? "#D30000" : "black"}`}}>Gender: </div>
                    <div className="infoGenderWrapper">
                        <button className="infoMaleButton"
                            style={{backgroundColor: `${gender === Genders.male ? "#D30000" : "#F2F2F2"}`}}
                            onClick={() => handleSelectGender(Genders.male)}>Male</button>
                        <button className="infoFemaleButton"
                            style={{backgroundColor: `${gender === Genders.female ? "#D30000" : "#F2F2F2"}`}}
                            onClick={() => handleSelectGender(Genders.female)}>Female</button>
                        <button className="infoOtherButton"
                            style={{backgroundColor: `${gender === Genders.other ? "#D30000" : "#F2F2F2"}`}}
                            onClick={() => handleSelectGender(Genders.other)}>Other</button>
                    </div>
                    <div className="infoTermsAndPrivacy">By submitting you are stating that you are at least 18 years of age and agree to our <a href="https://justlycardgame.com/TermsAndConditions.php" target="_blank" rel="noreferrer">Terms and Conditions</a> and <a href="https://justlycardgame.com/PrivacyPolicy.php" target="_blank" rel="noreferrer">Privacy Policy</a>.</div>
                    <button className="infoSubmitButton" onClick={submit}>Submit</button>
                    <div className="infoErrors">{errorMessage}</div>
                    {shouldShowDisclaimerDialog && <DisclaimerDialog isNewGame={isHost} startGame={startGame} joinGame={joinGame} closeDialog={closeDialog}/>}
                    {isSubmitting && <LoadingComponent/>}
                </div>
            </div>
        </div>
    );
};

export default InfoDialog;